import { communityAPIUrls } from 'apis/CompanyAPI/communities/communitiesApi';
import ApiBase from 'apis/ApiBase';
import useLazyResource from 'util/resource/useLazyResource';
import {
  CommunityCompanyNoteDTO,
  CreateCommunityCompanyNoteDTO,
  CreateNoteCommentDTO,
  NoteCommentDTO,
  UpdateNoteCommentDTO,
} from 'types/company/communityNotes';
import { invalidate } from 'hooks/useSWR';
import useResource from 'util/resource/useResource';

const api = ApiBase();

export function useCompanyNotes(communityId: number, companyId: number) {
  const key = communityAPIUrls.companyNotes.list(communityId, companyId);
  return useResource<CommunityCompanyNoteDTO[]>(key);
}

export function useCreateNote(
  communityId: number,
  companyId: number,
  options?: {
    onSuccess?: (note: CommunityCompanyNoteDTO) => void;
    onFailure?: (error: any) => void;
  },
) {
  return useLazyResource(
    (data: CreateCommunityCompanyNoteDTO) =>
      api.post<CommunityCompanyNoteDTO>(communityAPIUrls.companyNotes.create(communityId, companyId), data),
    {
      onSuccess: (createdNote: CommunityCompanyNoteDTO) => {
        // Optimistic update: add the new note to the cache
        const key = communityAPIUrls.companyNotes.list(communityId, companyId);
        invalidate(key, (currentNotes: CommunityCompanyNoteDTO[] | undefined) => {
          if (!currentNotes) return [createdNote];
          return [createdNote, ...currentNotes];
        });
        options?.onSuccess?.(createdNote);
      },
      onFailure: options?.onFailure,
    },
  );
}

export function useCreateComment(
  communityId: number,
  companyId: number,
  noteId: string,
  options?: {
    onSuccess?: () => void;
    onFailure?: (error: any) => void;
  },
) {
  return useLazyResource(
    (data: CreateNoteCommentDTO) =>
      api.post(communityAPIUrls.companyNotes.createComment(communityId, companyId, noteId), data),
    {
      onSuccess: createdComment => {
        // Optimistic update: add the comment to the note's comments array
        const key = communityAPIUrls.companyNotes.list(communityId, companyId);
        invalidate(key, (currentNotes: CommunityCompanyNoteDTO[] | undefined) => {
          if (!currentNotes) return currentNotes;
          return currentNotes.map(note => {
            if (note.id === noteId) {
              return {
                ...note,
                comments: [...note.comments, createdComment],
              };
            }
            return note;
          });
        });
        options?.onSuccess?.();
      },
      onFailure: options?.onFailure,
    },
  );
}

export function useUpdateComment(
  communityId: number,
  companyId: number,
  noteId: UUID,
  options?: {
    onSuccess?: () => void;
    onFailure?: (error: any) => void;
  },
) {
  return useLazyResource(
    (payload: { commentId: UUID; data: UpdateNoteCommentDTO }) =>
      api.patch<NoteCommentDTO>(
        communityAPIUrls.companyNotes.updateComment(communityId, companyId, noteId, payload.commentId),
        payload.data,
      ),
    {
      onSuccess: (updatedComment: NoteCommentDTO) => {
        // Optimistic update: update the comment in the note's comments array
        const key = communityAPIUrls.companyNotes.list(communityId, companyId);
        invalidate(key, (currentNotes: CommunityCompanyNoteDTO[] | undefined) => {
          if (!currentNotes) return currentNotes;
          return currentNotes.map(note => {
            if (note.id === noteId) {
              return {
                ...note,
                comments: note.comments.map(comment => (comment.id === updatedComment.id ? updatedComment : comment)),
              };
            }
            return note;
          });
        });
        options?.onSuccess?.();
      },
      onFailure: options?.onFailure,
    },
  );
}
